"use client"
import * as Checkbox from '@radix-ui/react-checkbox';
import * as Separator from '@radix-ui/react-separator';
import {
  Badge,
  Box,
  Button,
  Flex,
  Table,
  Text
} from '@radix-ui/themes';
import {
  BookOpen,
  Check,
  CheckCircle,
  Eye,
  Loader2,
  Pencil,
  RefreshCw,
  Settings,
  Trash2,
  User,
  XCircle
} from 'lucide-react';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ADMIN_LIST_FILTERS_DEBOUNCE_MS } from '@/lib/constants/adminListUi';
import { useCallback, useEffect, useMemo, useState } from 'react';

const FAQ_LIST_PAGE_SIZE = 10;

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQ {
  id: string;
  questions: FAQItem[];
  mappedTo?: string;
  mappedToBlogId?: string;
  mappedToBlogName?: string;
  mappedToServiceName?: string;
  status: 'Active' | 'Inactive';
  author: string;
  editor?: string;
  schemaEnabled: boolean;
  schemaMarkup?: Record<string, unknown>;
  slug: string;
  seo?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  formattedCreatedAt?: string;
  formattedUpdatedAt?: string;
}

interface FAQFilters {
  searchTerm: string;
  selectedStatus: string;
  selectedMapping: string;
  selectedDateRange: string;
  selectedAuthor: string;
  dateFrom?: string;
  dateTo?: string;
  blogSlug?: string;
  contentType?: 'blogs' | 'thought-leadership';
}

interface FAQListProps {
  filters: FAQFilters;
}

interface FAQResponse {
  faqs: FAQ[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const statusConfig = {
  Active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  Inactive: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
};



export default function FAQList({ filters }: FAQListProps) {

  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFAQs, setSelectedFAQs] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFAQs, setTotalFAQs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [_hasFiltersApplied, setHasFiltersApplied] = useState(false);

  const memoizedFilters = useMemo(() => filters, [filters]);
  const debouncedFilters = useDebouncedValue(memoizedFilters, ADMIN_LIST_FILTERS_DEBOUNCE_MS, {
    onDebouncedCommit: () => setCurrentPage(1),
  });

  const isFiltering = useMemo(() => {
    return (
      debouncedFilters.searchTerm.trim() !== '' ||
      debouncedFilters.selectedStatus !== 'All' ||
      debouncedFilters.selectedMapping !== 'All' ||
      debouncedFilters.selectedAuthor !== 'All' ||
      debouncedFilters.selectedDateRange !== 'All Time' ||
      debouncedFilters.dateFrom !== undefined ||
      debouncedFilters.dateTo !== undefined ||
      (debouncedFilters.blogSlug !== undefined && debouncedFilters.blogSlug !== '')
    );
  }, [debouncedFilters]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)} day${Math.floor(diffInDays) > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getRelativeTime = useCallback((dateString: string) => {
    return formatDate(dateString);
  }, []);

  const fetchFAQs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build query parameters from filters
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: String(FAQ_LIST_PAGE_SIZE),
      });

      if (debouncedFilters.searchTerm) {
        queryParams.append('search', debouncedFilters.searchTerm);
      }
      if (debouncedFilters.selectedStatus !== 'All') {
        queryParams.append('status', debouncedFilters.selectedStatus);
      }
      if (debouncedFilters.selectedMapping !== 'All') {
        queryParams.append('mappedTo', debouncedFilters.selectedMapping);
      }
      if (debouncedFilters.selectedAuthor !== 'All') {
        queryParams.append('author', debouncedFilters.selectedAuthor);
      }
      if (debouncedFilters.selectedDateRange !== 'All Time') {
        queryParams.append('dateRange', debouncedFilters.selectedDateRange);
      }
      if (debouncedFilters.dateFrom) {
        queryParams.append('dateFrom', debouncedFilters.dateFrom);
      }
      if (debouncedFilters.dateTo) {
        queryParams.append('dateTo', debouncedFilters.dateTo);
      }
      if (debouncedFilters.blogSlug) {
        queryParams.append('blogSlug', debouncedFilters.blogSlug);
      }
      if (debouncedFilters.contentType) {
        queryParams.append('contentType', debouncedFilters.contentType);
      }

      const response = await fetch(`/api/admin/faqs?${queryParams.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch FAQs');
      }
      const data = await response.json() as FAQResponse | { pagination?: { totalCount?: number; totalPages?: number } };

      // Add formatted dates for display
      const faqsWithFormattedDates = (data as FAQResponse).faqs.map(faq => ({
        ...faq,
        formattedCreatedAt: formatDate(faq.createdAt),
        formattedUpdatedAt: getRelativeTime(faq.updatedAt)
      }));

      setFaqs(faqsWithFormattedDates);
      const pagination = data.pagination;
      // Handle both pagination formats: FAQResponse format or alternative format
      if (pagination) {
        if ('totalCount' in pagination || 'totalPages' in pagination) {
          // Alternative pagination format
          setTotalFAQs((pagination as { totalCount?: number; totalPages?: number }).totalCount || 0);
          setTotalPages((pagination as { totalCount?: number; totalPages?: number }).totalPages || 0);
        } else {
          // Standard FAQResponse pagination format
          setTotalFAQs((pagination as FAQResponse['pagination']).total || 0);
          setTotalPages((pagination as FAQResponse['pagination']).pages || 0);
        }
      } else {
        setTotalFAQs(0);
        setTotalPages(0);
      }
      setHasFiltersApplied(isFiltering);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch FAQs');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, isFiltering, currentPage, getRelativeTime]);

  useEffect(() => {
    fetchFAQs();
  }, [currentPage, debouncedFilters, fetchFAQs]);

  const handleSelectAll = () => {
    if (selectedFAQs.length === faqs.length && faqs.length > 0) {
      setSelectedFAQs([]);
    } else {
      setSelectedFAQs(faqs.map(faq => faq.id));
    }
  };

  const handleSelectFAQ = (faqId: string) => {
    if (selectedFAQs.includes(faqId)) {
      setSelectedFAQs(selectedFAQs.filter(id => id !== faqId));
    } else {
      setSelectedFAQs([...selectedFAQs, faqId]);
    }
  };

  const handleBulkAction = async (action: 'activate' | 'deactivate' | 'delete') => {
    if (selectedFAQs.length === 0) return;

    try {
      const response = await fetch('/api/admin/faqs/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          faqIds: selectedFAQs
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to perform bulk action');
      }

      // Refresh the FAQ list
      await fetchFAQs();
      setSelectedFAQs([]);
      // Dispatch refresh event for dashboard stats
      window.dispatchEvent(new CustomEvent('faq-refresh'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to perform bulk action');
    }
  };

  const handleDeleteFAQ = async (faqId: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const response = await fetch(`/api/admin/faqs/${faqId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete FAQ');
      }

      // Refresh the FAQ list
      await fetchFAQs();
      // Dispatch refresh event for dashboard stats
      window.dispatchEvent(new CustomEvent('faq-refresh'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete FAQ');
    }
  };

  const getStatusConfig = (status: string) => {
    return statusConfig[status as keyof typeof statusConfig] || statusConfig.Inactive;
  };


  const tableBusy = loading;

  return (
    <Box className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Bulk Actions */}
      {selectedFAQs.length > 0 && (
        <Box className="px-6 py-3 bg-blue-50 border-b border-gray-200">
          <Flex justify="between" align="center">
            <Text size="2" className="text-blue-700">
              {selectedFAQs.length} FAQ{selectedFAQs.length > 1 ? 's' : ''} selected
            </Text>
            <Flex gap="2">
              <Button size="1" color="green" onClick={() => handleBulkAction('activate')}>
                Activate
              </Button>
              <Button size="1" color="yellow" onClick={() => handleBulkAction('deactivate')}>
                Deactivate
              </Button>
              <Button size="1" color="red" onClick={() => handleBulkAction('delete')}>
                Delete
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      {error && (
        <Box className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <Flex align="center" justify="between" gap="3" wrap="wrap">
            <Text size="2" className="text-red-800">{error}</Text>
            <Button size="1" variant="soft" color="red" onClick={() => void fetchFAQs()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Try again
            </Button>
          </Flex>
        </Box>
      )}

      <div className="relative min-h-[200px] px-6 py-4">
        {tableBusy && (
          <div
            className="absolute inset-6 z-10 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/75 backdrop-blur-[1px]"
            aria-busy="true"
            aria-live="polite"
          >
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <Text size="2" className="text-gray-600">
              {isFiltering ? 'Updating results…' : 'Loading FAQs…'}
            </Text>
          </div>
        )}
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>
              <Checkbox.Root
                checked={selectedFAQs.length === faqs.length && faqs.length > 0}
                onCheckedChange={handleSelectAll}
                className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <Checkbox.Indicator>
                  <Check className="h-3 w-3 text-white" />
                </Checkbox.Indicator>
              </Checkbox.Root>
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Blog/Page</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Total Questions</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Schema</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Author</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Editor</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {faqs.map((faq) => {
            const status = getStatusConfig(faq.status);
            const StatusIcon = status.icon;

            return (
              <Table.Row key={faq.id} className="hover:bg-gray-50">
                <Table.Cell>
                  <Checkbox.Root
                    checked={selectedFAQs.includes(faq.id)}
                    onCheckedChange={() => handleSelectFAQ(faq.id)}
                    className="h-4 w-4 rounded border border-gray-300 bg-white data-[state=checked]:bg-primary data-[state=checked]:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    <Checkbox.Indicator>
                      <Check className="h-3 w-3 text-white" />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                </Table.Cell>
                <Table.Cell>
                  <Box className="space-y-1">
                    {faq.mappedToBlogName && (
                      <div className="flex items-center space-x-2">
                        <BookOpen className="h-3 w-3 text-blue-400" />
                        <Text size="1" className="text-blue-900 bg-blue-100 px-2 py-1 rounded">
                          Blog: {faq.mappedToBlogName}
                        </Text>
                      </div>
                    )}
                    {faq.mappedToServiceName && (
                      <div className="flex items-center space-x-2">
                        <Settings className="h-3 w-3 text-gray-400" />
                        <Text size="1" className="text-gray-900 bg-gray-100 px-2 py-1 rounded">
                          Service: {faq.mappedToServiceName}
                        </Text>
                      </div>
                    )}
                    {!faq.mappedToBlogName && !faq.mappedToServiceName && (
                      <div className="flex items-center space-x-2">
                        <Settings className="h-3 w-3 text-gray-400" />
                        <Text size="1" className="text-gray-500">Not mapped</Text>
                      </div>
                    )}
                  </Box>
                </Table.Cell>
                <Table.Cell>
                  <Text size="2" weight="medium" className="text-gray-900">
                    {faq.questions?.length || 0}
                  </Text>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="soft" color={
                    faq.status === 'Active' ? 'green' : 'gray'
                  }>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {faq.status}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Badge variant="soft" color={
                    faq.schemaEnabled ? 'green' : 'gray'
                  }>
                    {faq.schemaEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="1" align="center">
                    <User className="h-4 w-4 text-gray-400" />
                    <Text size="2" className="text-gray-900">{faq.author}</Text>
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="1" align="center">
                    <User className="h-4 w-4 text-gray-400" />
                    <Text size="2" className="text-gray-900">{faq.editor || faq.author || '—'}</Text>
                  </Flex>
                </Table.Cell>
                <Table.Cell>
                  <Flex gap="5">
                    <Button variant="ghost" size="1" color="blue" asChild>
                      <a href={`/admin/faqs/${faq.id}`}>
                        <Eye className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button variant="ghost" size="1" color="blue" asChild>
                      <a href={`/admin/faqs/${faq.id}/edit`}>
                        <Pencil className="h-4 w-4" />
                      </a>
                    </Button>
                    <Button
                      variant="ghost"
                      size="1"
                      color="red"
                      onClick={() => handleDeleteFAQ(faq.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>

      {totalFAQs > 0 && (
        <>
          <Separator.Root className="h-px bg-gray-200" />
          <Box className="px-6 py-4 bg-gray-50">
            <Flex justify="between" align="center">
              <Text size="2" className="text-gray-700">
                Showing <Text weight="medium">{(currentPage - 1) * FAQ_LIST_PAGE_SIZE + 1}</Text> to{' '}
                <Text weight="medium">{Math.min(currentPage * FAQ_LIST_PAGE_SIZE, totalFAQs)}</Text> of{' '}
                <Text weight="medium">{totalFAQs}</Text> results
                {isFiltering && (
                  <span className="ml-2 text-blue-600 text-xs">
                    (filtered)
                  </span>
                )}
              </Text>
              <Flex gap="2">
                <Button
                  variant="outline"
                  size="1"
                  disabled={currentPage === 1 || tableBusy}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <Button
                      key={pageNum}
                      size="1"
                      variant={pageNum === currentPage ? "solid" : "outline"}
                      disabled={tableBusy}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="1"
                  disabled={currentPage === totalPages || tableBusy}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </Flex>
            </Flex>
          </Box>
        </>
      )}
      </div>
    </Box>
  );
}
